import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import {
  Category,
  CategoryTypeResponse as IdentityTypeResponse,
  CustomerInfo,
  GetTrainsRequest,
  LoginPayload,
  LoginResponse,
  Station,
  StationInfo,
  Train,
  TrainInfo,
  SearchParams,
  RevalidationStatusResponse,
  VerifyNinWithPhonePayload,
  VerifyNinWithPhoneResponse,
  VerifyOtpResponse,
} from 'src/app/core/models/breeze';
import { environment } from 'src/environments/environment';
import { HelperService } from './helper.service';
import { CookieService } from './cookie.service';
import { StorageService } from './storage.service';
import {
  BehaviorSubject,
  Observable,
  Subject,
  of,
  shareReplay,
  take,
  switchMap,
  map,
  catchError,
  finalize,
} from 'rxjs';
import { ErrorService } from './error.service';
import { AlertService } from './alert.service';
import { DialogService } from 'src/app/shared/services/dialog.service';
import { DatePipe } from '@angular/common';
import { Router } from '@angular/router';
import { NotificationService } from './notification.service';
// import { Flutterwave } from 'flutterwave-angular-v3';
import { countries } from '../../core/mock-data/you-verify-country-list';
import { EngageService } from './engage.service';
@Injectable({
  providedIn: 'root',
})
export class BreezeService {
  private scsoftUrl = `${environment.scsoftUrl.base}${environment.scsoftUrl.path}`;
  private scSoftGeneralUrl = `${environment.scsoftUrl.base}${environment.scsoftUrl.generalPath}`;
  private reportUrl = environment.reportUrl;
  private apiUrl: any = environment.apiUrl;
  private errorMessage = '';
  private isAuth: boolean = false;
  public isAuthListener = new Subject<boolean>();
  private stations: StationInfo[] = [];
  private stationsUpdate = new Subject<StationInfo[]>();
  private date: any;
  private dateUpdate = new Subject<any>();
  private trains: TrainInfo[] = [];
  private trainsUpdate = new Subject<TrainInfo[]>();
  private bookingId: any;
  private identityTypes: any;
  private identityTupesUpdate = new Subject<Category[]>();
  private searchParams: SearchParams | undefined = undefined;
  private searchParamsUpdate = new BehaviorSubject(this.searchParams);
  private mode: 'paid' | 'free' | 'fgn_free' = 'paid';
  private reference: string = '';
  private paymentBreakdown: any;
  private flwHandler: any = null;
  private isLoadingListener = new Subject<boolean>();
  private ticketHistory: any[] = [];
  private ticketHistoryUpdate = new Subject<any[]>();
  private UserverificationListener = new Subject<boolean>();
  private documentVerificationListener = new Subject<any>();
  private ninVerificationModalOpen = false;
  searchResultsListener = new Subject<any>();
  // Tracks whether the auth/get-auth report token is available - starts
  // true only if a token cookie already survived from earlier in the
  // session (e.g. a soft navigation without a full reload).
  reportAuthReady$: BehaviorSubject<boolean>;

  constructor(
    private http: HttpClient,
    private notification: NotificationService,
    private helperService: HelperService,
    private cookieService: CookieService,
    private storage: StorageService,
    private errorService: ErrorService,
    private dialogService: DialogService,
    private datePipe: DatePipe,
    private helpService: HelperService,
    private router: Router,
    private engageService: EngageService, // private flutterwave: Flutterwave
    private alertService: AlertService,
  ) {
    this.reportAuthReady$ = new BehaviorSubject<boolean>(
      !!this.cookieService.get(this.cookieService.REPORT_AUTH_TOKEN),
    );
  }

  getIsLoadingListener() {
    return this.isLoadingListener.asObservable();
  }

  setSearchParams(searchParams: any) {
    this.searchParams = searchParams;
    this.storage.set('searchParams', searchParams);
    this.searchParamsUpdate.next(this.searchParams);
  }

  getSearchParams() {
    if (this.searchParams) {
      return this.searchParamsUpdate.asObservable();
    }
    this.searchParams = this.storage.get('searchParams');
    this.searchParamsUpdate.next(this.searchParams);
    return this.searchParamsUpdate.asObservable();
  }

  register(registrationForm: any) {
    this.isLoadingListener.next(true);
    this.dialogService.openLoadingDialog();
    const regInfo = {
      name: registrationForm.name,
      family: registrationForm.family,
      emailAddress: registrationForm.email,
      password: this.helpService.encryptPassword(registrationForm.password),
      birthDate: registrationForm.birthDate,
      gender: registrationForm.gender,
      mobileNumber: registrationForm.phoneNumber,
      titleType: registrationForm.titleType,
      socialSecurityNumber: '',
      // socialSecurityNumber: registerInfo.socialSecurityNumber,
    };
    this.http
      .post<{ token: string }>(`${this.scsoftUrl}register`, regInfo)
      .subscribe(
        (res: any) => {
          const resultType = res.resultType;
          this.dialogService.closeLoadingModal();
          switch (resultType) {
            case 1:
              this.cookieService.remove('token');
              this.cookieService.set('token', res.token, 10000);
              this.notification.success(`check your email for OTP`);
              this.engageService.trackSignup(regInfo.emailAddress, {
                ...registrationForm,
                password: '',
                confirmPassword: '',
              });
              this.dialogService.openOTPDialog('user', regInfo.emailAddress);
              break;

            default:
              this.errorService.throwError(res.message);
              break;
          }
          this.isLoadingListener.next(false);
        },
        (err) => {
          this.isLoadingListener.next(false);

          this.dialogService.closeLoadingModal();
        },
      );
  }

  getIsAuthListener() {
    const token = this.cookieService.get(this.cookieService.COOKIE_NAME);
    this.isAuth = token ? true : false;
    this.isAuthListener.next(this.isAuth);
    // return this.isAuthListener.asObservable();
  }

  verfiyCaptcha(captcha_token: string) {
    return this.http.post(`${this.apiUrl}auth/verify-captcha/`, {
      captcha_token,
    });
  }

  // Authentication rework methods begin here
  login(loginPayload: LoginPayload) {
    this.dialogService.openLoadingDialog();
    // sterilizeData
    const data: {
      email: string;
      password: string;
    } = {
      email: loginPayload.email.toLocaleLowerCase(),
      password: this.helperService.encryptPassword(
        loginPayload.password,
      ) as string,
    };

    this.isLoadingListener.next(true);

    this.http
      .post<LoginResponse>(`${this.scsoftUrl}login`, data, {
        headers: { 'skip-error-interceptor': 'true' },
      })
      .pipe(
        switchMap((response) => {
          if (response.resultType !== 1) {
            return of({ response, authSession: undefined as any });
          }

          // TODO: 1. save token in cookies
          // TODO: 2. save customerUserInfo in localstorage
          this.cookieService.remove(this.cookieService.COOKIE_NAME);
          this.cookieService.set(
            this.cookieService.COOKIE_NAME,
            response.token,
            10000,
          );
          this.storage.setUser(response.customerInfo);
          this.isAuth = true;
          this.isAuthListener.next(this.isAuth);

          return this.registerAuthSession(data.email, response.token).pipe(
            map((authSession: any) => ({ response, authSession })),
            catchError((err) => {
              console.error('Failed to register auth session:', err);
              return of({ response, authSession: undefined as any });
            }),
          );
        }),
        finalize(() => this.isLoadingListener.next(false)),
      )
      .subscribe({
        next: ({ response, authSession }) => {
          switch (response.resultType) {
            case 1:
              if (authSession?.status && authSession?.token) {
                this.cookieService.set(
                  this.cookieService.REPORT_AUTH_TOKEN,
                  authSession.token,
                  10000,
                );
                this.reportAuthReady$.next(true);
                this.engageService.identify(response.customerInfo);

                this.notification.info('Login Successful');
                this.dialogService.closeLoginModal();
                this.getUserVerificationStatus(true);
              }
              this.dialogService.closeLoadingModal();
              break;

            case 3:
              // Case deactivated accounts
              this.errorMessage = `Your account has been blocked by NRC, due to illegal activities. Kindly call our customer care line for details.`;
              this.errorService.throwError(this.errorMessage);
              this.dialogService.closeLoadingModal();
              break;

            case 6:
              // case wrong password
              this.errorMessage = 'Invalid Username or Password';
              this.errorService.throwError(this.errorMessage);
              this.dialogService.closeLoadingModal();
              break;

            case 7:
              // Case User not found
              this.errorMessage = 'Invalid Username or Password';
              this.errorService.throwError(this.errorMessage);
              this.dialogService.closeLoadingModal();
              break;

            case 8:
              // Email not verified - hands off to newVerificationCode(),
              // which owns closing the loading modal once it resolves.
              this.newVerificationCode(loginPayload.email);
              // show get otp dialog
              break;

            case 9:
              // Case User not found
              this.errorMessage = 'Invalid Username or Password';
              this.errorService.throwError(this.errorMessage);
              this.dialogService.closeLoadingModal();
              break;

            case 15:
              // Case User not found
              this.errorMessage =
                response.message || 'User logged in on another device';
              this.errorService.throwError(this.errorMessage);
              this.dialogService.closeLoadingModal();
              break;

            case 26:
              // Case User not found
              this.errorMessage = 'Invalid Username or Password';
              this.errorService.throwError(this.errorMessage);
              this.dialogService.closeLoadingModal();
              break;

            default:
              this.isAuth = false;
              this.errorMessage =
                response.message || 'Invalid Username or Password';
              this.errorService.throwError(this.errorMessage);
              this.dialogService.closeLoadingModal();
              break;
          }
        },
        error: () => {
          this.dialogService.closeLoadingModal();
        },
      });
    // return this.isAuthListener.asObservable();
  }

  // Fire-and-forget: registers the SCSoft auth session with the report
  // backend right after login. Failure here shouldn't disrupt the user's
  // login, so it's silently logged rather than surfaced.
  private registerAuthSession(email: string, scToken: string) {
    return this.http.post(
      `${this.apiUrl}auth/get-auth/`,
      { email, sc_token: scToken },
      {
        headers: {
          'skip-error-interceptor': 'true',
          'skip-client-key': 'true',
        },
      },
    );
  }

  logOut() {
    this.cookieService.remove('token');
    this.cookieService.remove(this.cookieService.REPORT_AUTH_TOKEN);
    this.reportAuthReady$.next(false);
    this.isAuth = false;
    this.isAuthListener.next(this.isAuth);
    this.storage.clearUserData();
    this.router.navigate(['/']);
  }

  newVerificationCode(email: string) {
    const data = {
      email,
    };
    // this.isUploading = true;
    this.http
      .post(
        `${this.scsoftUrl}resend-verfication-code`,
        // `${this.apiUrl}nigeriacc/api/webPortalApplicationService/verify`,
        data,
      )
      .subscribe(
        (res: any) => {
          // todo: save token
          this.cookieService.remove('token');
          this.cookieService.set('token', res.token, 10000);
          this.dialogService.openOTPDialog('user', email);
          this.dialogService.closeLoginModal();
          this.dialogService.closeLoadingModal();
        },
        (err: any) => {
          // this.notification.error(`:: An error Occured`);
        },
      );
  }

  forgoPassword(email: any) {
    this.dialogService.openLoadingDialog();
    this.http.post(`${this.scsoftUrl}forgotPassword`, { email }).subscribe(
      (res: any) => {
        if (res.resultType === 1) {
          this.cookieService.remove('token');
          this.cookieService.set('token', res.token, 10000);
          // this.notification.success(
          //   `Success!! please check your email for OTP`
          // );
          // skip verification using OTP
          // todo: close get email dialog
          this.dialogService.closeForgotPasswordModal();
          this.dialogService.openOTPDialog('password', email);
          // this.router.navigate(["/reset-password"]);
        } else if (res.resultType === 8) {
          this.newVerificationCode(email);
        } else {
          if (res.message) {
            this.errorMessage = res.message;
            this.errorService.throwError(this.errorMessage);
          } else {
            // this.notification.error(`:: An error Occurred`);
          }
        }
        this.dialogService.closeLoadingModal();
      },
      (err: any) => {
        // this.notification.error(`:: An error Occurred`);
        this.dialogService.closeLoadingModal();
      },
    );
  }

  youVerifySDKDocumentCapture(data: { first_name: string; phone_no: any }) {
    const documentCapture = new (window as any).YouverifySDK.documentCapture({
      // sandboxEnvironment: false,
      publicMerchantKey: '64dcd5f16153cbc062fbc817',
      personalInformation: {
        firstName: data.first_name,
      },
      appearance: {
        greeting:
          'We will need to capture your document. It will only take a moment.',
        actionText: 'Capture Document',
        buttonBackgroundColor: '#007d53',
        buttonTextColor: '#e6f2ee',
        primaryColor: '#007d53',
      },
      // dev: false,
      countries: countries,
      onSuccess: (res: any) => {
        // result received from document
        this.submitDocVerification({
          phone_no: data.phone_no,
          data: res,
          platform: 'WEB',
        });
      },
      onFailure: (res: any) => {
        // could not get result from document
      },
      onClose: (res: any) => {
        // optional
        // custom logic here
      },
    });
    try {
      documentCapture.initialize();
    } catch (error) {}

    documentCapture.start();
  }

  submitDocVerification(data: { phone_no: any; data: any; platform: 'WEB' }) {
    this.http
      .post(`${this.apiUrl}services/n-data/verified-document-capture/`, data)
      .subscribe(
        (res) => {
          this.documentVerificationListener.next(res);
        },
        (err) => {
          this.documentVerificationListener.error(err);
        },
      );
  }

  getDocUploadResult() {
    return this.documentVerificationListener.asObservable();
  }

  verifyOTP(inputCode: any) {
    this.isLoadingListener.next(true);
    this.dialogService.openLoadingDialog();
    const token = this.cookieService.get('token');
    this.http
      .post(
        `${this.scsoftUrl}verifyForPassword`,
        {
          inputCode,
          token: token,
        },
        // this.httpNotGETOptions
      )
      .subscribe(
        (response: any) => {
          this.isLoadingListener.next(false);
          if (response.resultType === 1) {
            this.dialogService.closeOTPModal();
            this.dialogService.openResetPasswordDialog();
            // this.router.navigate(["/reset-password"]);
          } else {
            this.errorMessage = response?.message;
            this.errorService.throwError(this.errorMessage);
          }
          this.dialogService.closeLoadingModal();
        },
        (err: any) => {
          this.isLoadingListener.next(false);
          this.dialogService.closeLoadingModal();
          // this.notification.error(`:: An error Occurred`);
        },
      );
  }

  verifyUser(inputCode: any, email = '') {
    const data = {
      inputCode,
      token: this.cookieService.get(this.cookieService.COOKIE_NAME),
    };
    this.isLoadingListener.next(true);
    this.dialogService.openLoadingDialog();
    this.http.post(`${this.scsoftUrl}verify`, data).subscribe(
      (res: any) => {
        if (res.resultType === 1) {
          this.notification.success(`:: Success!!`);
          this.dialogService.closeOTPModal();
          this.dialogService.closeSignupModal();
          this.dialogService.openLoginDialog();
          this.engageService.trackVerification(email);
        } else {
          this.errorService.throwError(res.message);
        }
        this.isLoadingListener.next(false);
        this.dialogService.closeLoadingModal();
      },
      (err: any) => {
        this.dialogService.closeLoadingModal();
        this.notification.error(`:: An error Occured`);
        this.isLoadingListener.next(false);
        this.errorService.throwError('An error Occured');
      },
    );
  }

  resetPassword(passwordPayload: any) {
    this.dialogService.openLoadingDialog();
    const data = {
      newPassword: this.helperService.encryptPassword(
        passwordPayload.newPassword,
      ),
      temporaryToken: this.cookieService.get('token'),
    };
    this.http
      .post(
        `${this.scsoftUrl}resetPassword`,
        data,
        // this.httpNotGETOptions
      )
      .subscribe(
        (res: any) => {
          if (res.resultType === 1) {
            this.dialogService.closeResetPasswordModal();
            this.dialogService.openLoginDialog();
            // this.notification.info(`Please login with your new password`);
          } else {
            this.errorService.throwError(res.message);
          }
          this.dialogService.closeLoadingModal();
        },
        (err: any) => {
          this.dialogService.closeLoadingModal();
        },
      );
  }

  getServerTime() {
    this.http.get(`${this.apiUrl}services/current_times`).subscribe(
      (response: any) => {
        this.date = response;
        this.dateUpdate.next({ ...this.date });
      },
      (err) => {
        return;
      },
    );
    return this.dateUpdate.asObservable();
  }

  getMessage() {
    return this.http.get(`${this.apiUrl}services/global/popup/`);
    // .pipe(
    //     take(1)
    // )
    // .subscribe((response: any) => {
    //     this.message = response.value
    //     this.messageUpdated.next(this.message)
    // }, err => {
    //     this.messageUpdated.next("")
    // })
    // return this.messageUpdated.asObservable()
  }

  getStations() {
    this.http.post<Station>(`${this.scsoftUrl}getAllStations`, {}).subscribe(
      (data) => {
        const trainConfig = data.trainConstantConfiguration;
        this.storage.set('trainConfig', trainConfig);
        this.stations = data.stationInfo;
        this.stationsUpdate.next([...this.stations]);
      },
      (err) => {
        // this.notification.error(
        //   `An error occurred, please check your internet`
        // );
        this.stations = [];
        this.stationsUpdate.next([...this.stations]);
      },
    );
    return this.stationsUpdate.asObservable();
  }

  getTrains(data: GetTrainsRequest) {
    this.http
      .post<Train>(`${this.scsoftUrl}searchTrainSeatsV1`, {
        ...data,
        token: this.cookieService.get(this.cookieService.COOKIE_NAME),
      })
      .subscribe({
        next: (response) => {
          this.dialogService.closeLoadingTripsModal();
          setTimeout(() => {
            if (response.resultType == 1) {
              this.trains = response.tickets;
              this.trainsUpdate.next([...this.trains]);
              this.searchResultsListener.next(this.trains);
            } else if (response.resultType === 16) {
              // todo: 1. forcelogout
              this.logOut();
              this.dialogService.openLoginDialog();
              // this.notification.info(
              //   `An error occurred while searching for trains, please try again`
              // );
            } else {
              this.trains = [];
              this.searchResultsListener.next(this.trains);
              this.trainsUpdate.next([...this.trains]);
              // this.notification.info(
              //   `An error occurred while searching for trains`
              // );
            }
          }, 500);
          if (!this.router.url.includes('search-results')) {
            this.router.navigate(['search-results']);
          }
        },
        error: (err) => {
          this.dialogService.closeLoadingTripsModal();
          this.trains = [];
          this.searchResultsListener.next(this.trains);
          this.trainsUpdate.next([...this.trains]);
          // this.notification.error(
          //   `An error occurred, please check your internet`
          // );
        },
      });

    // return this.trainsUpdate.asObservable();
  }

  getUserverificationListener() {
    return this.UserverificationListener.asObservable();
  }

  getUserVerificationStatus(promptIfUnverified = false) {
    const profile = this.storage.get('user');
    const isAuthenticated = !!this.cookieService.get(
      this.cookieService.COOKIE_NAME,
    );
    if (!profile?.mobileNumber) {
      // User info is not available in localStorage (e.g. it was cleared). If a
      // session token is still present the session is unusable, so log the user
      // out; otherwise just mark them unverified.
      if (isAuthenticated) {
        this.logOut();
      } else {
        this.UserverificationListener.next(false);
      }
      return;
    }
    this.getRevalidationStatus(profile.mobileNumber)
      .pipe(shareReplay(1))
      .subscribe({
        next: (response) => {
          if (response?.success && response?.is_revalidated) {
            this.UserverificationListener.next(true);
            this.dialogService.closeNinVerificationDialog();
          } else {
            this.UserverificationListener.next(false);
            if (promptIfUnverified) {
              this.openNinVerificationModal();
            }
          }
        },
        error: () => {
          // The status check itself failed (API/network error) - this is
          // different from the user genuinely being unverified, so don't
          // open the verification form; tell them the check failed instead,
          // with a Refresh button that just re-runs this same check.
          this.UserverificationListener.next(false);
          this.alertService.verificationUnavailableModal(() =>
            this.getUserVerificationStatus(promptIfUnverified),
          );
        },
      });
  }

  openNinVerificationModal() {
    if (this.ninVerificationModalOpen) {
      return;
    }
    this.dialogService.closeNinVerificationPromptDialog();
    this.ninVerificationModalOpen = true;
    const modalRef = this.dialogService.openNinVerificationDialog();
    modalRef.result.finally(() => {
      this.ninVerificationModalOpen = false;
      this.getUserVerificationStatus(true);
    });
  }

  extractBackendMessage(payload: unknown): string | undefined {
    if (!payload || typeof payload !== 'object') {
      return undefined;
    }

    const data = payload as Record<string, unknown>;
    const candidates = [
      data['message'],
      data['Message'],
      data['detail'],
      data['errorMessage'],
      data['title'],
    ];

    for (const candidate of candidates) {
      if (typeof candidate === 'string' && candidate.trim()) {
        return candidate;
      }
    }

    return undefined;
  }

  getRevalidationStatus(phone_number: string) {
    const ninVerificationUrl = environment.ninVerificationUrl;
    return this.http.post<RevalidationStatusResponse>(
      `${ninVerificationUrl}services/youverify/revalidation-status/`,
      { phone_number },
      {
        headers: { 'skip-error-interceptor': 'true' },
      },
    );
  }

  verifyNinWithPhone(payload: VerifyNinWithPhonePayload) {
    return this.http.post<VerifyNinWithPhoneResponse>(
      `${this.apiUrl}services/youverify/verify-nin-with-phone/`,
      payload,
      {
        headers: { 'skip-error-interceptor': 'true' },
      },
    );
  }

  verifyNinOtp(phone_number: string, otp: string) {
    return this.http.post<VerifyOtpResponse>(
      `${this.apiUrl}services/youverify/verify-otp/`,
      { phone_number, otp },
      {
        headers: { 'skip-error-interceptor': 'true' },
      },
    );
  }

  verifyPhone(requestPayload: { user_mode: string; phone_number: string }) {
    return this.http.post(`${this.apiUrl}services/verify_phone/`, {
      ...requestPayload,
      platform: 'WEB',
    });
  }

  checkSeatAvailability(requestPayload: {
    trip_label: string;
    travel_timestamp: string;
    phone_numbers: string;
  }) {
    return this.http.post(`${this.apiUrl}app/new_txn/validate-passenger/`, {
      ...requestPayload,
    });
  }

  verifyNINV2(customerUserInfo: any, user_mode: 'B' | 'R') {
    const data = {
      ...customerUserInfo,
      user_mode,
      platform: 'WEB',
    };

    return this.http.post(`${this.apiUrl}services/n-data/verify-user/`, data);
    // this.isLoadingListener.next(true);
  }

  generateReference() {
    const timeStamp = new Date().getTime();
    const result = Math.random().toString(36).substring(2, 15);
    return (this.reference = `WEB-${timeStamp}${result}`);
  }

  reserveTickets(
    reservationInfo: any,
    paymentMethod:
      | 'FLUTTERWAVE'
      | 'TRANSFER'
      | 'FULL_VOUCHER_DISCOUNT' = 'FLUTTERWAVE',
  ) {
    this.generateReference();
    const searchParams: SearchParams = this.storage.get('searchParams');
    const user = this.storage.get('user');
    const trip = this.storage.get('selectedTrip');
    const data = {
      seats: reservationInfo.seats.map((element: any) => {
        return { seatNumber: element.seatNumber, coachCode: element.coachCode };
      }),
      totalPrice: reservationInfo.totalPrice,
      trainCode: this.storage.get('selectedTrip').trainCode,
      destinationStation1: searchParams.destinationStation1,
      sourceStation1: searchParams.sourceStation1,
      isLimitCheck: 1,
      paymentType: 4,
      customerId: user.userID,
      isTicketReIssueEnableFlag: 1,
      date: this.datePipe.transform(
        this.storage.get('searchParams').date,
        'YYYYMMddHHmmss',
      ),
      token: this.cookieService.get(this.cookieService.COOKIE_NAME),
    };
    this.http.post(`${this.scsoftUrl}reserveTrainSeats`, data).subscribe(
      (response: any) => {
        switch (response.resultType) {
          case 1: {
            // successful
            this.bookingId = response.bookingId;
            this.storage.set('bookingId', this.bookingId);
            // todo1: after successful reservation, initiate payment
            // todo1: after successful payment, verify payment
            // todo1: after successful verification, confirm tickets
            let reserved_selected_seat = reservationInfo.seats.map(
              (element: any) => {
                return {
                  customerName: `${element.firstname} ${element.lastname}`,
                  customerId: user.userID,
                  customerCategoryId: 0,
                  email: element.email,
                  coachCode: element.coachCode,
                  seatNumber: element.seatNumber,
                  phoneNumber: element.phoneNumber,
                  productCode: element.productCode,
                  // ...element,
                };
              },
            );
            const getPaymentData: any = {
              sc_customer_userid: user.userID,
              sc_customer_name: user.name,
              sc_customer_email: user.emailAddress,
              sc_customer_phone: user.mobileNumber,
              sc_booking_id: this.bookingId,
              destination_station_code: searchParams.destinationStation1,
              source_station_code: searchParams.sourceStation1,
              reserved_ticket_fare: reservationInfo.totalPrice,
              reserved_number_of_passengers: reservationInfo?.seats?.length,
              reserved_selected_seat: reserved_selected_seat,
              sc_trip_type: trip?.tripNumber,
              sc_trip_date: searchParams.date,
              sc_token: this.cookieService.get(this.cookieService.COOKIE_NAME),
              // sc_ref_id: this.reference,
              payment_gateway: '',
              platform: 'WEB',
              platform_version: environment.version,
            };
            if (reservationInfo.voucher_code) {
              (getPaymentData as unknown as any).voucher_code =
                reservationInfo.voucher_code;
            }
            // this.getPaymentBreakdown(getPaymentData, reservationInfo);
            if (paymentMethod === 'TRANSFER') {
              getPaymentData.payment_gateway = 'SECUREPAY';
              this.initiatePaymentTransfer(getPaymentData, reservationInfo);
              // getPaymentData.sc_ref_id = this.reference;
            } else if (paymentMethod === 'FLUTTERWAVE') {
              getPaymentData.payment_gateway = 'FLUTTERWAVE';
              this.getPaymentBreakdown(getPaymentData, reservationInfo);
            } else if (paymentMethod === 'FULL_VOUCHER_DISCOUNT') {
              this.payWithFullDiscountVoucher(getPaymentData, reservationInfo);
            }
            break;
          }
          case 6:
            // Internal Server Issue
            this.dialogService.closeLoadingModal();
            this.notification.error(
              response.message ||
                'Something went wrong on our end. Please try again in a few moments.',
            );
            break;
          case 16:
            // todo: force logout
            this.logOut();
            this.dialogService.closeLoadingModal();
            this.notification.info(
              `User logged in elsewhere, please login again`,
            );
            break;
          case 17:
            // Not Valid Booking - reservation expired or booking ID invalid
            this.dialogService.closeLoadingModal();
            this.notification.error(
              response.message ||
                'This booking is no longer valid. Please start your booking again.',
            );
            break;
          case 25:
            // Invalid Request Body
            this.dialogService.closeLoadingModal();
            this.notification.error(
              response.message ||
                'Some booking details are missing or invalid. Please review and try again.',
            );
            break;
          case 30:
            // Seat is Not Available for Booking
            this.dialogService.closeLoadingModal();
            this.notification.error(
              response.message ||
                'The selected seat is no longer available. Please choose another seat.',
            );
            break;
          case 31:
            // Seat Booking Limit Exceeded per Train
            this.dialogService.closeLoadingModal();
            this.notification.error(
              response.message ||
                'The seat booking limit for this train has been exceeded.',
            );
            break;
          case 48:
            // User Ticket Booking Limit Exceeded with NIN
            this.dialogService.closeLoadingModal();
            this.notification.error(
              response.message ||
                'You have reached the maximum ticket booking limit for this NIN. No further bookings are allowed until this limit is cleared.',
            );
            break;
          default:
            this.dialogService.closeLoadingModal();
            this.notification.error(response.message);
            break;
        }
      },
      (err) => {
        this.notification.error(err.error.message);
        this.dialogService.closeLoadingModal();
        return;
      },
    );
  }

  reserveTicketsForTransfer(reservationInfo: any): Observable<any> {
    this.generateReference(); // Ensure reference generation
    const searchParams = this.storage.get('searchParams');
    const user = this.storage.get('user');
    const trip = this.storage.get('selectedTrip');

    const data = {
      seats: reservationInfo.seats.map((element: any) => {
        return { seatNumber: element.seatNumber, coachCode: element.coachCode };
      }),
      totalPrice: reservationInfo.totalPrice,
      trainCode: this.storage.get('selectedTrip').trainCode,
      destinationStation1: searchParams.destinationStation1,
      sourceStation1: searchParams.sourceStation1,
      isLimitCheck: 1,
      paymentType: 4,
      customerId: user.userID,
      isTicketReIssueEnableFlag: 1,
      date: this.datePipe.transform(
        this.storage.get('searchParams').date,
        'YYYYMMddHHmmss',
      ),
      token: this.cookieService.get(this.cookieService.COOKIE_NAME),
    };

    // Return the observable directly from the http.post
    return this.http.post(`${this.scsoftUrl}reserveTrainSeats`, data);
  }

  // reserveTicketsForTransfer(reservationInfo: any) {
  //   this.generateReference();
  //   const searchParams: SearchParams = this.storage.get('searchParams');
  //   const user = this.storage.get('user');
  //   const trip = this.storage.get('selectedTrip');
  //   const data = {
  //     seats: reservationInfo.seats.map((element: any) => {
  //       return { seatNumber: element.seatNumber, coachCode: element.coachCode };
  //     }),
  //     totalPrice: reservationInfo.totalPrice,
  //     trainCode: this.storage.get('selectedTrip').trainCode,
  //     destinationStation1: searchParams.destinationStation1,
  //     sourceStation1: searchParams.sourceStation1,
  //     isLimitCheck: 1,
  //     paymentType: 4,
  //     customerId: user.userID,
  //     isTicketReIssueEnableFlag: 1,
  //     date: this.datePipe.transform(
  //       this.storage.get('searchParams').date,
  //       'YYYYMMddHHmmss'
  //     ),
  //     token: this.cookieService.get(this.cookieService.COOKIE_NAME),
  //   };
  //   this.http.post(`${this.scsoftUrl}reserveTrainSeats`, data).subscribe(
  //     (response: any) => {
  //       let message = response.message;
  //       if (response.resultType === 1) {
  //         // successful
  //         this.bookingId = response.bookingId;
  //         // todo1: after successful reservation, initiate payment
  //         // todo1: after successful payment, verify payment
  //         // todo1: after successful verification, confirm tickets
  //         let reserved_selected_seat = reservationInfo.seats.map(
  //           (element: any) => {
  //             return {
  //               customerName: `${element.firstname} ${element.lastname}`,
  //               customerId: user.userID,
  //               customerCategoryId: 0,
  //               email: element.email,
  //               coachCode: element.coachCode,
  //               seatNumber: element.seatNumber,
  //               phoneNumber: element.phoneNumber,
  //               productCode: element.productCode,
  //               // ...element,
  //             };
  //           }
  //         );
  //         const getPaymentData = {
  //           sc_customer_userid: user.userID,
  //           sc_customer_name: user.name,
  //           sc_customer_email: user.emailAddress,
  //           sc_customer_phone: user.mobileNumber,
  //           sc_booking_id: this.bookingId,
  //           destination_station_code: searchParams.destinationStation1,
  //           source_station_code: searchParams.sourceStation1,
  //           reserved_ticket_fare: reservationInfo.totalPrice,
  //           reserved_number_of_passengers: reservationInfo?.seats?.length,
  //           reserved_selected_seat: reserved_selected_seat,
  //           sc_trip_type: trip?.tripNumber,
  //           sc_trip_date: searchParams.date,
  //           sc_token: this.cookieService.get(this.cookieService.COOKIE_NAME),
  //           sc_ref_id: this.reference,
  //           payment_gateway: 'FLUTTERWAVE',
  //           platform: 'WEB',
  //           platform_version: environment.version,
  //         };
  //         //this.getPaymentBreakdown(getPaymentData, reservationInfo);
  //         //this.initiatePaymentTransfer(getPaymentData, reservationInfo);

  //         const useTransferPayment = true;
  //         if (useTransferPayment) {
  //           this.initiatePaymentTransfer(getPaymentData, reservationInfo);
  //         } else {
  //           this.getPaymentBreakdown(getPaymentData, reservationInfo);
  //         }
  //       } else if (response.resultType === 16) {
  //         // todo: force logout
  //         this.logOut();
  //         this.dialogService.closeLoadingModal();
  //         this.notification.info(
  //           `User logged in elsewhere, please login again`
  //         );
  //       } else if (response.resultType === 30) {
  //         this.dialogService.closeLoadingModal();
  //         this.notification.error(response.message);
  //         // seat taken
  //       } else if (response.resultType === 31) {
  //         // Total number of seats exceeded
  //         this.dialogService.closeLoadingModal();
  //         this.notification.error(response.message);
  //       } else if (response.resultType === 48) {
  //         // Total number of seats exceeded
  //         this.dialogService.closeLoadingModal();
  //         this.notification.error(
  //           response.message || 'Maximum booking capacity exceeded'
  //         );
  //       } else {
  //         // this.toastr.error(
  //         this.dialogService.closeLoadingModal();
  //         this.notification.error(response.message);
  //         //   `${message || "Seat(s) not Reserved"}`,
  //         //   "An error Occured",
  //         //   {
  //         //     timeOut: 5000,
  //         //     closeButton: true,
  //         //   }
  //         // );
  //       }
  //     },
  //     (err) => {
  //       this.notification.error(err.error.message);
  //       this.dialogService.closeLoadingModal();
  //       return;
  //     }
  //   );
  // }

  getPaymentMode() {
    return this.http
      .get(`${this.apiUrl}services/global/mode/`)
      .subscribe((paymentmode: any) => {
        this.mode = paymentmode.value;
        if (this.mode.toLowerCase() === 'fgn_free') {
          this.notification.info(
            'Great News',
            'Ticket prices have been slashed from now till Jan 4, courtesy of FG and NRC. Happy holidays 🎉',
          );
        }
      });
  }

  private redirectToPaymentLink(paymentLink: string) {
    this.dialogService.closeLoadingModal();
    this.notification.success('Redirecting to payment gateway...');
    window.location.href = paymentLink;
  }

  initiatePaymentTransfer(data: any, reservationInfo: any) {
    this.storage.set('reservationInfo', reservationInfo);

    // const paymentInitData = {
    //   paymentMethod: 'Transfer',
    //   amount: reservationInfo.totalPrice,
    //   // reference: data.sc_ref_id,
    //   bookingId: data.sc_booking_id,
    //   timestamp: new Date().toISOString(),
    //   passengerCount: reservationInfo.seats?.length || 0,
    // };

    // this.engageService.trackEvent(
    //   'Transfer Payment Link Generated',
    //   paymentInitData,
    // );

    return this.http
      .post(`${this.apiUrl}app/new_txn_v2/initiate-payment-transfer/`, data)
      .subscribe(
        (res: any) => {
          if (res && res.status === 'success' && res.data) {
            this.paymentBreakdown = res.data.breakdown;

            // this.engageService.trackEvent('Transfer Payment Link Success', {
            //   ...paymentInitData,
            //   paymentLink: res.data.payment_link,
            //   mode: this.paymentBreakdown.mode,
            // });

            switch (this.paymentBreakdown.mode.toLowerCase()) {
              case 'free':
                this.confirmFreeSeats(reservationInfo);
                this.engageService.trackBookingSuccess({
                  bookingId: data.sc_booking_id,
                  amount: 0,
                  mode: 'free',
                  passengerCount: reservationInfo.seats?.length || 0,
                  timestamp: new Date().toISOString(),
                });
                break;
              case 'fgn_free':
                this.notification.info(
                  'Great News',
                  'Ticket prices have been slashed from now till Jan 4, courtesy of FG and NRC. Happy holidays 🎉',
                );
                this.reference = res.data.sc_ref_id;
                this.redirectToPaymentLink(res.data.payment_link);
                break;
              case 'paid':
                if (!(this.paymentBreakdown?.flw_amount > 0)) {
                  this.notification.warning('A pricing error has occured');
                  return;
                }
                this.reference = res.data.sc_ref_id;
                this.redirectToPaymentLink(res.data.payment_link);
                break;
              default:
                this.notification.error('Unknown payment mode');
                this.dialogService.closeLoadingModal();
                break;
            }
          } else {
            this.notification.error('Failed to initiate payment transfer');
            this.dialogService.closeLoadingModal();
          }
        },
        (err: any) => {
          // this.notification.error('Error', 'An unknown error occured');
          this.dialogService.closeLoadingModal();
          this.cancelReservation();
        },
      );
  }

  getPaymentBreakdown(data: any, reservationInfo: any) {
    return this.http.post(`${this.apiUrl}app/new_txn_v2/`, data).subscribe(
      // return this.http.post(`${this.apiUrl}app/new_txn/`, data).subscribe(
      (res: any) => {
        this.paymentBreakdown = res;
        switch (this.paymentBreakdown.mode.toLowerCase()) {
          case 'free':
            this.confirmFreeSeats(reservationInfo);
            break;
          case 'fgn_free':
            this.notification.info(
              'Great News',
              'Ticket prices have been slashed from now till Jan 4, courtesy of FG and NRC. Happy holidays 🎉',
            );
            this.handleFLW(reservationInfo);
            break;
          case 'paid':
            if (!(this.paymentBreakdown?.flw_amount > 0)) {
              this.notification.warning('A pricing error has occured');
              return;
            }
            this.reference = res.sc_ref_id;
            this.handleFLW(reservationInfo);
            break;

          default:
            break;
        }
        // if (this.paymentBreakdown.mode.toLowerCase() === 'free') {
        //   this.confirmFreeSeats(reservationInfo);
        //   return;
        // }
      },
      (err) => {
        // this.notification.error('Error', 'An unknown error occured');
        this.dialogService.closeLoadingModal();
        this.cancelReservation();
      },
    );
  }

  payWithFullDiscountVoucher(data: any, reservationInfo: any) {
    return this.http
      .post(`${this.apiUrl}app/new_txn/complete-with-voucher/`, data)
      .subscribe(
        (res) => {
          this.dialogService.closeLoadingModal();
          const user = this.storage.get('user');

          const selectedSeats = reservationInfo.seats.map((element: any) => {
            return {
              customerName: `${element.firstname} ${element.lastname}`,
              phoneNumber: `${element.phoneNumber}`,
              email: `${element.email}`,
              productCode: element.productCode,
              coachCode: element.coachCode,
              seatNumber: element.seatNumber,
              customerCategoryId: 0,
              customerId: user.userID,
            };
          });

          this.confirmSeatsByFullDiscountVoucher({
            bookingId: this.bookingId,
            rrn: this.reference,
            selectedSeats,
            token: this.cookieService.get(this.cookieService.COOKIE_NAME),
          });
        },
        (err) => {
          // this.notification.error('Error', 'An unknown error occured');
          this.dialogService.closeLoadingModal();
          this.cancelReservation();
        },
      );
  }

  handleFLW(reservationInfo: any) {
    const user = this.storage.get('user');

    const sum =
      this.paymentBreakdown.the_nrc_main_share +
      this.paymentBreakdown.the_nrc_medical +
      this.paymentBreakdown.the_nrc_insurance +
      this.paymentBreakdown.the_sid_share +
      this.paymentBreakdown.the_tps_share +
      this.paymentBreakdown.the_nrc_stamp_duty;

    if (sum > this.paymentBreakdown.flw_amount) {
      this.notification.warning('A pricing error has occured');
      return;
    }

    this.flwHandler = (window as any).FlutterwaveCheckout({
      public_key: environment.flwPubKey,
      tx_ref: this.reference,
      // Reducing amount for test purpose
      amount: this.paymentBreakdown.flw_amount,
      currency: 'NGN',
      payment_options: 'card, banktransfer, ussd',
      redirect_url: '',
      subaccounts: [
        // Nrc share
        {
          id: 'RS_AC2A469BEA6AF375ECAB95E5A2E23000',
          transaction_charge_type: 'flat_subaccount',
          transaction_charge: this.paymentBreakdown.the_nrc_main_share,
        },
        // Nrc Medical
        {
          id: 'RS_2911E04B561C98D41E3CA357CD1546C2',
          transaction_charge_type: 'flat_subaccount',
          transaction_charge: this.paymentBreakdown.the_nrc_medical,
        },
        // Nrc Insurance
        {
          id: 'RS_69CB28CF508587CE03165621215826D4',
          transaction_charge_type: 'flat_subaccount',
          transaction_charge: this.paymentBreakdown.the_nrc_insurance,
        },
        // SID
        {
          id: 'RS_6B102B2E6211BF0E961CB6E9C076AF14',
          transaction_charge_type: 'flat_subaccount',
          transaction_charge: `${this.paymentBreakdown.the_sid_share}`,
        },
        // TPS
        {
          id: 'RS_5A052D02DD72BB3C5D55D58F9252E96E',
          transaction_charge_type: 'flat_subaccount',
          transaction_charge: `${this.paymentBreakdown.the_tps_share}`,
        },
        // STAMP DUTY
        {
          id: 'RS_C033B7B1AECCD90FF695BB8A9A86BC4F',
          transaction_charge_type: 'flat_subaccount',
          transaction_charge: `${this.paymentBreakdown.the_nrc_stamp_duty}`,
        },
      ],
      meta: {
        consumer_id: user.userID,
        ...user,
      },
      customer: {
        email: user.emailAddress,
        phone_number: user.mobileNumber,
        name: `${user.name} ${user.family}`,
      },
      configurations: {
        session_duration: 10, //setting session duration to 10mis //Session timeout in minutes (maxValue: 1440 minutes)
        max_retry_attempt: 0, //No retry allowed //Max retry (int)
      },
      // customizations: this.raveOptions.customizations,
      callback: (PaymentCalldata: any) => {
        setTimeout(() => {
          this.paymentSuccess(PaymentCalldata, reservationInfo);
        }, 0);
      },
      onclose: () => this.closeFlwDialog(),
    });
  }

  closeFlwDialog() {
    if (this.flwHandler !== null) {
      setTimeout(() => {
        this.flwHandler.close();
        this.dialogService.closeLoadingModal();
      }, 1000);
      return;
    }
    this.dialogService.closeLoadingModal();
  }

  paymentSuccess(data: any, reservationInfo: any) {
    // todo: call confirm tickets
    const user = this.storage.get('user');

    const selectedSeats = reservationInfo.seats.map((element: any) => {
      return {
        customerName: `${element.firstname} ${element.lastname}`,
        phoneNumber: `${element.phoneNumber}`,
        email: `${element.email}`,
        productCode: element.productCode,
        coachCode: element.coachCode,
        seatNumber: element.seatNumber,
        customerCategoryId: 0,
        customerId: user.userID,
      };
    });

    this.confirmSeatsByFlutterwave({
      bookingId: this.bookingId,
      rrn: this.reference,
      selectedSeats,
      token: this.cookieService.get(this.cookieService.COOKIE_NAME),
    });
  }

  verifySecurepayPayment(rrn: string) {
    // todo: call confirm tickets
    const user = this.storage.get('user');
    const reservationInfo: any = this.storage.get('reservationInfo');
    const bookingId: any = this.storage.get('bookingId') || this.bookingId;

    const selectedSeats = (reservationInfo.seats || []).map((element: any) => {
      return {
        customerName: `${element.firstname} ${element.lastname}`,
        phoneNumber: `${element.phoneNumber}`,
        email: `${element.email}`,
        productCode: element.productCode,
        coachCode: element.coachCode,
        seatNumber: element.seatNumber,
        customerCategoryId: 0,
        customerId: user.userID,
      };
    });

    // this.engageService.trackEvent('Transfer Payment Verification', {
    //   reference: rrn,
    //   bookingId: bookingId,
    //   timestamp: new Date().toISOString(),
    // });

    this.confirmSeatsBySecurepay({
      bookingId: bookingId,
      rrn: rrn,
      selectedSeats,
      token: this.cookieService.get(this.cookieService.COOKIE_NAME),
    });
  }

  confirmFreeSeats(data: any) {
    const user = this.storage.get('user');
    const selectedSeats = data.seats.map((element: any) => {
      return {
        customerName: `${element.firstname} ${element.lastname}`,
        phoneNumber: `${element.phoneNumber}`,
        email: `${element.email}`,
        productCode: element.productCode,
        coachCode: element.coachCode,
        seatNumber: element.seatNumber,
        customerCategoryId: 0,
        customerId: user.userID,
      };
    });
    const payload = {
      bookingId: this.bookingId,
      rrn: this.reference,
      selectedSeats,
      token: this.cookieService.get(this.cookieService.COOKIE_NAME),
    };
    this.http.post(`${this.apiUrl}app/free/confirm_seat/`, payload).subscribe(
      (res) => {
        this.router.navigate(['/ticket-history']);
        this.notification.success('Success', 'Ticket purchase successful');
        this.closeFlwDialog();
      },
      (err) => {
        this.notification.error('Error', 'Error purchasing ticket(s)');
        this.dialogService.closeLoadingModal();
      },
    );
  }

  confirmSeatsByFlutterwave(data: any) {
    this.http.post(`${this.apiUrl}app/flw/confirm_seat/`, data).subscribe(
      (res: any) => {
        // this.dialogService.closeLoadingModal();
        switch (res.code) {
          case 200:
            this.notification.warning(
              'Pending',
              `
              We can't confirm the status of your transaction now, a mail containing your ticket(s) and transaction status will be sent upon confirmation. Please don't try again till after 10 minutes if you don’t receive a mail.
              `,
            );
            break;
          case 201:
            this.notification.success('Success', 'Ticket purchase successful');
            break;
          case 400:
            this.notification.error(
              'Declined',
              'Unable to verify status of your transaction',
            );
            break;
          default:
            break;
        }
        this.router.navigate(['/ticket-history']);
        // this.notification.success('Success', 'Ticket purchase successful');
        this.closeFlwDialog();
      },
      (err) => {
        this.dialogService.closeLoadingModal();
        this.notification.warning(
          'Pending',
          `
          We can't confirm the status of your transaction now, a mail containing your ticket(s) and transaction status will be sent upon confirmation. Please don't try again till after 10 minutes if you don’t receive a mail.
          `,
        );
      },
    );
  }

  confirmSeatsBySecurepay(data: any) {
    this.http.post(`${this.apiUrl}app/securepay/confirm_seat/`, data).subscribe(
      (res: any) => {
        // const paymentData = {
        //   paymentMethod: 'Transfer',
        //   amount: this.paymentBreakdown?.flw_amount || 0,
        //   reference: data.rrn,
        //   currency: 'NGN',
        //   bookingId: data.bookingId,
        //   timestamp: new Date().toISOString(),
        // };

        // const bookingData = {
        //   bookingId: data.bookingId,
        //   passengerCount: data.selectedSeats?.length || 0,
        //   totalAmount: this.paymentBreakdown?.flw_amount || 0,
        //   reference: data.rrn,
        //   timestamp: new Date().toISOString(),
        // };
        switch (res.code) {
          case 200:
            this.notification.warning(
              'Pending',
              `
              We can't confirm the status of your transaction now, a mail containing your ticket(s) and transaction status will be sent upon confirmation. Please don't try again till after 10 minutes if you don’t receive a mail.
              `,
            );
            // this.engageService.trackEvent('Transfer Payment Pending', {
            //   ...paymentData,
            //   status: 'pending',
            // });
            break;
          case 201:
            this.notification.success('Success', 'Ticket purchase successful');
            // Track successful payment
            // this.engageService.trackPaymentSuccess({
            //   ...paymentData,
            //   status: 'success',
            // });
            // // Track booking success
            // this.engageService.trackBookingSuccess({
            //   ...bookingData,
            //   status: 'confirmed',
            // });
            break;
          case 400:
            this.notification.error(
              'Declined',
              'Unable to verify status of your transaction',
            );
            // this.engageService.trackEvent('Transfer Payment Failed', {
            //   ...paymentData,
            //   status: 'failed',
            //   reason: 'declined',
            // });
            break;
          default:
            break;
        }
        this.router.navigate(['/ticket-history']);

        // this.notification.success('Success', 'Ticket purchase successful');
      },
      (err) => {
        this.dialogService.closeLoadingModal();
        this.notification.warning(
          'Pending',
          `
          We can't confirm the status of your transaction now, a mail containing your ticket(s) and transaction status will be sent upon confirmation. Please don't try again till after 10 minutes if you don’t receive a mail.
          `,
        );
        this.router.navigate(['/ticket-history']);

        const paymentData = {
          paymentMethod: 'Transfer',
          amount: this.paymentBreakdown?.flw_amount || 0,
          reference: data.rrn,
          currency: 'NGN',
          bookingId: data.bookingId,
          status: 'error',
          error: err.message,
          timestamp: new Date().toISOString(),
        };
        this.engageService.trackEvent('Transfer Payment Error', paymentData);
      },
    );
  }

  confirmSeatsByFullDiscountVoucher(data: any) {
    this.http.post(`${this.apiUrl}app/voucher/confirm_seat/`, data).subscribe(
      (res: any) => {
        // this.dialogService.closeLoadingModal();
        switch (res.code) {
          case 200:
            this.notification.success('Success', 'Ticket purchase successful');
            break;
          case 201:
            this.notification.success('Success', 'Ticket purchase successful');
            break;
          case 400:
            this.notification.error(
              'Declined',
              'Unable to verify status of your transaction',
            );
            break;
          default:
            break;
        }
        this.router.navigate(['/ticket-history']);

        // this.notification.success('Success', 'Ticket purchase successful');
      },
      (err) => {
        this.dialogService.closeLoadingModal();
        this.notification.warning(
          'Pending',
          `
          We can't confirm the status of your transaction now, a mail containing your ticket(s) and transaction status will be sent upon confirmation. Please don't try again till after 10 minutes if you don’t receive a mail.
          `,
        );
        this.router.navigate(['/ticket-history']);
      },
    );
  }

  cancelReservation() {
    this.http
      .post(`${this.apiUrl}app/new-cancel/reserved_seat/`, {
        bookingId: this.bookingId,
        token: this.cookieService.get(this.cookieService.COOKIE_NAME),
      })
      .subscribe(() => {});
  }

  getTicketHistory() {
    this.http
      .post(`${this.scsoftUrl}ticketBookingHistory`, {
        token: this.cookieService.get(this.cookieService.COOKIE_NAME),
      })
      .subscribe(
        (data: any) => {
          if (data.resultType === 1) {
            this.ticketHistory = data.trainTicketBookingHistory;
            this.ticketHistoryUpdate.next([...this.ticketHistory]);
          } else if (data.resultType === 16) {
            // todo: notify user of the error
            // todo: force logout
            this.logOut();
            this.dialogService.openLoginDialog();
          } else {
            // this.ticketHistory = [];
            // this.ticketHistoryUpdate.next([...this.ticketHistory]);
          }
        },
        (err) => {
          return;
        },
      );
    return this.ticketHistoryUpdate.asObservable();
  }

  getTicketHistoryWithDate(fromDate: string, toDate: string) {
    this.http
      .post(`${this.scsoftUrl}ticketBookingHistoryWithDate`, {
        fromDate,
        toDate,
        token: this.cookieService.get(this.cookieService.COOKIE_NAME),
      })
      .subscribe(
        (data: any) => {
          if (data.resultType === 1) {
            this.ticketHistory = data.trainTicketBookingHistory;
            this.ticketHistoryUpdate.next([...this.ticketHistory]);
          } else if (data.resultType === 16) {
            // todo: notify user of the error
            // todo: force logout
            this.logOut();
            this.dialogService.openLoginDialog();
          } else {
            // this.ticketHistory = [];
            // this.ticketHistoryUpdate.next([...this.ticketHistory]);
          }
        },
        (err) => {
          return;
        },
      );
    return this.ticketHistoryUpdate.asObservable();
  }

  getIdentityTypes() {
    this.http
      .post<IdentityTypeResponse>(`${this.scsoftUrl}categoryType`, {
        date: '220525',
      })
      .subscribe(
        (data: any) => {
          if (data.resultType === 1) {
            this.identityTypes = data.categories;
            // FEATURE: stick to just NIN
            // this.identityTypes.push({ label: 'Document Verification' });
            this.identityTupesUpdate.next([...this.identityTypes]);
          } else {
            this.identityTypes = [];
            this.identityTupesUpdate.next([...this.identityTypes]);
          }
        },
        (err) => {
          this.identityTypes = [];
          this.identityTupesUpdate.next([...this.identityTypes]);
        },
      );
    return this.identityTupesUpdate.asObservable();
  }

  getCoachTypes() {
    return this.http.get(`${this.scSoftGeneralUrl}fetchActiveCoachTypes`);
  }

  getProductList() {
    return this.http.get(`${this.scSoftGeneralUrl}getProductList`);
  }
}
